import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useJazyk } from '../context/LanguageContext';
import { getAvatarImage } from '../components/Layout';
import { SectionHeader } from '../components/SectionHeader';
import { useLocations } from '../hooks/useLocations';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { toast } from 'sonner';
import { Plus, Search, MapPin, MessageCircle, Trash2, Heart, Send, Filter, ArrowUpRight, ArrowDownLeft, Pencil } from 'lucide-react';

import { API } from '../lib/api';

const SupportPage = () => {
  const { user, isAdmin } = useAuth();
  const { t, datum } = useJazyk();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [myServices, setMyServices] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [postTypeFilter, setPostTypeFilter] = useState('all');
  const [showMyServices, setShowMyServices] = useState(false);
  // CZ / WORLD toggle for the filter
  const [filterCountry, setFilterCountry] = useState('CZ');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editServiceDialogOpen, setEditServiceDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [offer, setOffer] = useState('');
  const [need, setNeed] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('none');
  const [serviceType, setServiceType] = useState('other');
  // Nabídka / Poptávka switch for the "Přidat" form
  const [postType, setPostType] = useState('offer');
  // CZ / WORLD toggle for new offer form
  const [formCountry, setFormCountry] = useState('CZ');

  const { allLocations } = useLocations();
  const czLocations = allLocations.filter(l => l.country === 'CZ');
  const worldLocations = allLocations.filter(l => l.country === 'WORLD');
  const formLocations = formCountry === 'CZ' ? czLocations : worldLocations;

  useEffect(() => { fetchServices(); fetchMyServices(); fetchServiceTypes(); }, []);
  useEffect(() => { fetchServices(); }, [locationFilter, typeFilter, postTypeFilter, filterCountry]);

  const fetchServices = async () => {
    try {
      const params = { search: searchQuery };
      if (locationFilter !== 'all') params.location = locationFilter;
      if (typeFilter !== 'all') params.service_type = typeFilter;
      if (filterCountry) params.country = filterCountry;
      const r = await axios.get(`${API}/services`, { params });
      setServices(r.data);
    } catch {} finally { setLoading(false); }
  };
  const fetchMyServices = async () => { try { const r = await axios.get(`${API}/services/my`); setMyServices(r.data); } catch {} };
  const fetchServiceTypes = async () => { try { const r = await axios.get(`${API}/service-types`); setServiceTypes(r.data); } catch {} };
  const handleSearch = (e) => { e.preventDefault(); fetchServices(); };

  const handleCreateService = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/services`, {
        offer, need, description,
        location: location === 'none' ? '' : location,
        service_type: serviceType,
        post_type: postType
      });
      toast.success(t(postType === 'request' ? 'pod.vytvorenoPoptavka' : 'pod.vytvorenoNabidka'));
      setDialogOpen(false); setOffer(''); setNeed(''); setDescription(''); setLocation('none'); setServiceType('other'); setPostType('offer');
      fetchServices(); fetchMyServices();
    } catch { toast.error(t('pod.chybaVytvorit')); }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm(t('pod.opravduSmazat'))) return;
    try { await axios.delete(`${API}/services/${id}`); toast.success(t('pod.smazano')); fetchServices(); fetchMyServices(); }
    catch { toast.error(t('pod.chybaSmazat')); }
  };

  const handleOpenServiceEdit = (svc) => {
    setEditingService(svc);
    setOffer(svc.offer || '');
    setNeed(svc.need || '');
    setDescription(svc.description || '');
    setLocation(svc.location || 'none');
    setServiceType(svc.service_type || 'other');
    setEditServiceDialogOpen(true);
  };

  const handleUpdateService = async (e) => {
    e.preventDefault();
    if (!editingService) return;
    try {
      await axios.put(`${API}/services/${editingService.id}`, {
        offer, need, description,
        location: location === 'none' ? '' : location,
        service_type: serviceType,
      });
      toast.success(t('pod.aktualizovano'));
      setEditServiceDialogOpen(false); setEditingService(null);
      setOffer(''); setNeed(''); setDescription(''); setLocation('none'); setServiceType('other');
      fetchServices(); fetchMyServices();
    } catch (err) { toast.error(err.response?.data?.detail || t('pod.chybaUlozit')); }
  };

  const handleReply = (s) => {
    if (!user?.email_verified) return; // prevent opening reply for unverified
    setSelectedService(s);
    const druh = t(s.post_type === 'request' ? 'pod.konceptPoptavka' : 'pod.konceptNabidka');
    setReplyMessage(t('pod.konceptOdpovedi', { co: druh, nazev: s.offer || s.need }));
    setReplyDialogOpen(true);
  };
  const handleSendReply = async () => {
    if (!selectedService || !replyMessage.trim()) return;
    try { await axios.post(`${API}/messages`, { to_user_id: selectedService.user_id, content: replyMessage }); toast.success(t('pod.zpravaOdeslana')); setReplyDialogOpen(false); }
    catch { toast.error(t('pod.chybaOdeslat')); }
  };

  const baseList = showMyServices ? myServices : services;
  const displayed = postTypeFilter === 'request'
    ? baseList.filter(s => s.post_type === 'request')
    : baseList;

  const filterLocations = filterCountry === 'CZ' ? czLocations : worldLocations;
  const locationOpts = [
    { id: 'all', name: t(filterCountry === 'CZ' ? 'pod.celaCR' : 'pod.celySvet') },
    ...filterLocations,
  ];

  return (
    <>
    <div className="min-h-screen py-6" data-testid="support-page">
      <div className="pride-bar mb-6" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          sectionKey="support"
          klicTitulku="nav.support"
          klicPodtitulku="pod.podtitulek"
          defaultColor="#F5A9B8"
        />

        {/* Post type tabs */}
        <div className="flex items-center gap-2 mb-4">
          {[
            { value: 'all', label: 'pod.vse' },
            { value: 'offer', label: 'pod.nabidky', icon: ArrowUpRight },
            { value: 'request', label: 'pod.poptavky', icon: ArrowDownLeft },
          ].map(zalozka => {
            const Icon = zalozka.icon;
            return (
              <button
                key={zalozka.value}
                onClick={() => setPostTypeFilter(zalozka.value)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  postTypeFilter === zalozka.value
                    ? 'bg-bloom-violet text-white shadow-sm'
                    : 'bg-white border border-border text-bloom-sub hover:border-bloom-violet/50 hover:text-bloom-violet'
                }`}
                data-testid={`post-type-filter-${zalozka.value}`}
              >
                {Icon && <Icon className="w-3.5 h-3.5" />}{t(zalozka.label)}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub/50" />
              <Input placeholder={t('obecne.hledatPriklad')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="support-search-input" />
            </div>
            <Button type="submit" variant="secondary" className="bg-bloom-violet/10 text-bloom-violet hover:bg-bloom-violet/20 hidden sm:flex">{t('obecne.hledat')}</Button>
          </form>
          <div className="flex gap-2 flex-wrap">
            <div className="hidden md:flex gap-2 items-center">
              {/* CZ / WORLD country toggle */}
              <div className="flex rounded-lg border border-border overflow-hidden">
                {[{ v: 'CZ', l: 'pod.cesko' }, { v: 'WORLD', l: 'pod.svet' }].map(x => (
                  <button
                    key={x.v}
                    type="button"
                    onClick={() => { setFilterCountry(x.v); setLocationFilter('all'); }}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${filterCountry === x.v ? 'bg-bloom-violet text-white' : 'bg-white text-bloom-sub hover:bg-muted'}`}
                    data-testid={`filter-country-${x.v}`}
                  >
                    {t(x.l)}
                  </button>
                ))}
              </div>
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger className="w-40" data-testid="location-filter">
                  <MapPin className="w-4 h-4 mr-1 text-bloom-violet" /><SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {locationOpts.map(o => <SelectItem key={o.id || o.name} value={o.id === 'all' ? 'all' : o.name}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-36" data-testid="type-filter"><Filter className="w-4 h-4 mr-1 text-bloom-mint" /><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('pod.vsechnyTypy')}</SelectItem>
                  {serviceTypes.map(typ => <SelectItem key={typ.id} value={typ.id}>{typ.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant={showMyServices ? "default" : "outline"} onClick={() => setShowMyServices(!showMyServices)}
              className={showMyServices ? "bg-bloom-violet text-white" : ""} data-testid="my-services-toggle">
              {t('pod.moje', { pocet: myServices.length })}
            </Button>
            {user?.email_verified ? (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-bloom-mint text-white hover:bg-bloom-mint/90" data-testid="create-service-btn">
                  <Plus className="w-4 h-4 mr-1" />{t('pod.pridat')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-serif text-bloom-text">{t(postType === 'request' ? 'pod.novaPoptavka' : 'pod.novaNabidka')}</DialogTitle>
                  <DialogDescription>{t(postType === 'request' ? 'pod.popisPoptavky' : 'pod.popisNabidky')}</DialogDescription>
                </DialogHeader>
                <div className="h-1 bg-gradient-to-r from-[#5BCEFA] via-[#F5A9B8] to-[#5BCEFA] rounded mb-4" />
                <form onSubmit={handleCreateService} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-bloom-text">{t('pod.typPrispevku')}</Label>
                    <div className="flex rounded-lg border border-border overflow-hidden">
                      {[{ v: 'offer', l: 'pod.nabidka' }, { v: 'request', l: 'pod.poptavka' }].map(x => (
                        <button type="button" key={x.v} onClick={() => setPostType(x.v)}
                          className={`flex-1 py-1.5 text-xs font-medium transition-colors ${postType === x.v ? 'bg-bloom-violet text-white' : 'bg-white text-bloom-sub hover:bg-muted'}`}
                          data-testid={`form-post-type-${x.v}`}>
                          {t(x.l)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-bloom-text">{postType === 'request' ? <>{t('pod.coNabizim')} <span className="text-bloom-sub font-normal text-xs">{t('pod.volitelne')}</span></> : t('pod.coNabizimP')}</Label>
                    <Input value={offer} onChange={(e) => setOffer(e.target.value)} placeholder={t('pod.priklOffer')} required={postType !== 'request'} data-testid="service-offer-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-bloom-text">{postType === 'request' ? t('pod.coHledamP') : <>{t('pod.coHledamVymenou')} <span className="text-bloom-sub font-normal text-xs">{t('pod.volitelne')}</span></>}</Label>
                    <Input value={need} onChange={(e) => setNeed(e.target.value)} placeholder={t('pod.priklNeed')} required={postType === 'request'} data-testid="service-need-input" />
                    <p className="text-[11px] text-bloom-sub/70">{t(postType === 'request' ? 'pod.poptavkaSeZobrazi' : 'pod.nabidkaSeZobrazi')}</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-bloom-text">{t('pod.popis')}</Label>
                    <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} required placeholder={t('pod.popisPriklad')} data-testid="service-description-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-bloom-text">{t('pod.oblast')}</Label>
                    {/* CZ / Svět toggle */}
                    <div className="flex rounded-lg border border-border overflow-hidden mb-2">
                      {[{ v: 'CZ', l: 'pod.cesko' }, { v: 'WORLD', l: 'pod.svet' }].map(x => (
                        <button type="button" key={x.v} onClick={() => { setFormCountry(x.v); setLocation('none'); }}
                          className={`flex-1 py-1.5 text-xs font-medium transition-colors ${formCountry === x.v ? 'bg-bloom-violet text-white' : 'bg-white text-bloom-sub hover:bg-muted'}`}
                          data-testid={`form-country-${x.v}`}>
                          {t(x.l)}
                        </button>
                      ))}
                    </div>
                    <Select value={location} onValueChange={setLocation}>
                      <SelectTrigger data-testid="service-location-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('pod.nechciUvadet')}</SelectItem>
                        {formLocations.map(o => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-bloom-text">{t('pod.kategorie')}</Label>
                    <Select value={serviceType} onValueChange={setServiceType}>
                      <SelectTrigger data-testid="service-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {serviceTypes.map(typ => <SelectItem key={typ.id} value={typ.id}>{typ.name}</SelectItem>)}
                        {!serviceTypes.some(typ => typ.id === 'other') && <SelectItem value="other">{t('pod.ostatni')}</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full bg-bloom-violet text-white" data-testid="service-submit-btn">
                    {t(postType === 'request' ? 'pod.pridatPoptavku' : 'pod.pridatNabidku')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            ) : (
              <Link to="/profile" className="text-sm text-amber-600 hover:text-amber-700 font-medium">
                {t('pod.overEmail')}
              </Link>
            )}
          </div>
        </div>

        <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-bloom-text">{t('pod.odpovedetNaPrispevek')}</DialogTitle>
              <DialogDescription>{t('pod.napisteAutorovi')}</DialogDescription>
            </DialogHeader>
            {selectedService && (
              <div className="space-y-4">
                <div className="p-3 bg-muted/50 rounded-lg border border-border">
                  <div className="flex gap-2 mb-1 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full bg-bloom-pride-pink/10 text-bloom-pride-pink text-xs">{selectedService.offer}</span>
                    <span className="text-bloom-sub/50 text-xs">↔</span>
                    <span className="px-2 py-0.5 rounded-full bg-bloom-pride-blue/10 text-bloom-pride-blue text-xs">{selectedService.need}</span>
                  </div>
                  <p className="text-sm text-bloom-sub">{t('pod.od', { kdo: selectedService.username })}</p>
                </div>
                <Textarea value={replyMessage} onChange={(e) => setReplyMessage(e.target.value)} rows={4} data-testid="reply-message-input" />
                <Button onClick={handleSendReply} className="w-full bg-bloom-violet text-white" disabled={!replyMessage.trim()} data-testid="send-reply-btn">
                  <Send className="w-4 h-4 mr-1" />{t('pod.odeslatZpravu')}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : displayed.length === 0 ? (
          <Card className="bg-white border-border/50"><CardContent className="p-12 text-center">
            <Heart className="w-10 h-10 text-bloom-pride-pink/40 mx-auto mb-3" />
            <h3 className="font-serif text-lg font-semibold text-bloom-text mb-1">
              {t(showMyServices ? 'pod.zadneMoje' : 'pod.zadneNalezeny')}
            </h3>
            <p className="text-sm text-bloom-sub">
              {t(showMyServices ? 'pod.vytvortePrvni' : 'pod.zkusteFiltry')}
            </p>
          </CardContent></Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayed.map((s) => {
              const isExpired = s.expires_at && new Date(s.expires_at) < new Date();
              return (
              <Card key={s.id} className={`bg-white border-border/50 card-hover ${isExpired ? 'opacity-75' : ''}`} data-testid={`service-card-${s.id}`}>
                <CardHeader className="pb-2 pt-4 px-4">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => navigate(`/users/${s.user_id}`)}
                      className="flex items-center gap-2 hover:opacity-80 transition-opacity group"
                      data-testid={`author-link-${s.id}`}
                    >
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={getAvatarImage(s.avatar, s.custom_avatar)} />
                        <AvatarFallback className="bg-bloom-violet text-white text-xs">{s.username?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="text-left">
                        <p className="text-sm font-medium text-bloom-text group-hover:text-bloom-violet transition-colors">{s.username}</p>
                        <p className="text-xs text-bloom-sub">{datum(s.created_at)}</p>
                      </div>
                    </button>
                    <div className="flex items-center gap-1">
                      {isExpired && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-600">{t('pod.expirovano')}</span>
                      )}
                      {!isExpired && (
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${s.post_type === 'request' ? 'bg-bloom-pride-blue/10 text-bloom-pride-blue' : 'bg-bloom-pride-pink/10 text-bloom-pride-pink'}`}>
                          {t(s.post_type === 'request' ? 'pod.hledam' : 'pod.nabidka')}
                        </span>
                      )}
                      {(user?.id === s.user_id || isAdmin) && (
                        <>
                          <Button variant="ghost" size="icon" className="text-bloom-violet h-7 w-7" title={t('obecne.upravit')}
                            onClick={() => handleOpenServiceEdit(s)} data-testid={`edit-service-${s.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-destructive h-7 w-7" onClick={() => handleDeleteService(s.id)} data-testid={`delete-service-${s.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {s.post_type === 'request' ? (
                      <>
                        {s.need && <span className="px-2 py-0.5 rounded-full bg-bloom-pride-blue/10 text-bloom-pride-blue text-xs font-medium">{t('pod.hledamCo', { co: s.need })}</span>}
                        {s.offer && <span className="px-2 py-0.5 rounded-full bg-bloom-pride-pink/10 text-bloom-pride-pink text-xs font-medium">{t('pod.nabizimCo', { co: s.offer })}</span>}
                      </>
                    ) : (
                      <>
                        {s.offer && <span className="px-2 py-0.5 rounded-full bg-bloom-pride-pink/10 text-bloom-pride-pink text-xs font-medium">{t('pod.nabizimCo', { co: s.offer })}</span>}
                        {s.need && <span className="px-2 py-0.5 rounded-full bg-bloom-pride-blue/10 text-bloom-pride-blue text-xs font-medium">{t('pod.hledamCo', { co: s.need })}</span>}
                      </>
                    )}
                  </div>
                  <p className="text-sm text-bloom-sub line-clamp-2 md:line-clamp-3 mb-2">{s.description}</p>
                  <div className="flex items-center gap-2 text-xs text-bloom-sub">
                    {s.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-bloom-violet" />{s.location}</span>}
                  </div>
                  {isExpired && showMyServices && (
                    <p className="text-xs text-amber-600 mt-2">{t('pod.expirovala')}</p>
                  )}
                  {!isExpired && user?.id !== s.user_id && user?.email_verified && (
                    <Button variant="outline" className="w-full mt-3 text-bloom-violet border-bloom-violet/20 hover:bg-bloom-violet/5" onClick={() => handleReply(s)} data-testid={`reply-service-${s.id}`}>
                      <MessageCircle className="w-4 h-4 mr-1" />{t('pod.odpovedet')}
                    </Button>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>

    {/* Edit service dialog */}
    <Dialog open={editServiceDialogOpen} onOpenChange={open => { setEditServiceDialogOpen(open); if (!open) { setEditingService(null); setOffer(''); setNeed(''); setDescription(''); setLocation('none'); setServiceType('other'); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-bloom-text">{t('pod.upravitPrispevek')}</DialogTitle>
          <DialogDescription>{t('pod.upravtePrispevek')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleUpdateService} className="space-y-4 pt-2">
          <div>
            <Label>{t('pod.nabizimP')}</Label>
            <Input value={offer} onChange={e => setOffer(e.target.value)} placeholder={t('pod.coNabizitePriklad')} required data-testid="edit-service-offer" />
          </div>
          <div>
            <Label>{t('pod.hledamPotrebuji')}</Label>
            <Input value={need} onChange={e => setNeed(e.target.value)} placeholder={t('pod.coHledatePriklad')} data-testid="edit-service-need" />
          </div>
          <div>
            <Label>{t('pod.popis')}</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder={t('pod.popisPriklad')} rows={3} data-testid="edit-service-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('pod.kategorie')}</Label>
              <Select value={serviceType} onValueChange={setServiceType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{serviceTypes.map(typ => <SelectItem key={typ.id} value={typ.id}>{typ.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('pod.lokalita')}</Label>
              <Select value={location} onValueChange={setLocation}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('pod.celaCR')}</SelectItem>
                  {allLocations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button type="submit" className="w-full bg-bloom-violet text-white" data-testid="edit-service-submit">
            {t('pod.ulozitZmeny')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  </>
  );
};

export default SupportPage;
